(($) => {

	/**
	 * Gulp include scripts
	 *
	 * @link https://www.npmjs.com/package/gulp-include
	 */
	(() => {
		/**
		 * Card Grid — Load More.
		 *
		 * The template renders every card visible and the button hidden (see
		 * block-card-grid.php); this script inverts that on load. Nothing is
		 * withheld from a visitor without JavaScript, and nothing is offered that
		 * JavaScript isn't there to deliver.
		 *
		 * Cards are hidden with the `hidden` attribute rather than by clipping or
		 * collapsing them, so they leave the accessibility tree and the tab order
		 * together — a card a sighted visitor can't see is not one a keyboard can
		 * still land in.
		 *
		 * On each press the first newly revealed card takes focus. Without that,
		 * focus stays on the button, which has just travelled down the page ahead
		 * of content the visitor has no way of knowing arrived; the polite live
		 * region says how much of the set is now showing.
		 */
		document.querySelectorAll('.block-card-grid[data-load-more]').forEach((grid) => {
			const items = grid.querySelector('.block-card-grid__items');
			const footer = grid.querySelector('.block-card-grid__footer');
			const button = grid.querySelector('[data-load-more-button]');
			const status = grid.querySelector('[data-load-more-status]');
	
			if (!items || !footer || !button) return;
	
			// ACF wraps inner blocks in `.acf-innerblocks-container`, which is
			// `display: contents` globally (see _global.scss) -- so the cards lay
			// out as children of the grid but sit a level deeper in the DOM.
			const container = items.querySelector(':scope > .acf-innerblocks-container') || items;
			const cards = Array.from(container.children);
			const batch = Math.max(1, parseInt(grid.dataset.loadMore, 10) || 1);
			const initial = Math.max(1, parseInt(grid.dataset.loadMoreInitial, 10) || batch);
	
			// Nothing to reveal: leave every card showing and the button hidden,
			// rather than printing a control that would do nothing.
			if (cards.length <= initial) return;
	
			let shown = initial;
	
			const paint = () => {
				cards.forEach((card, index) => {
					card.hidden = index >= shown;
				});
			};
	
			const reveal = () => {
				const from = shown;
				const firstNew = cards[from];
	
				shown = Math.min(cards.length, shown + batch);
				paint();
	
				// Number each card within the batch so the stylesheet can stagger
				// them off one delay; the class is dropped again once it has run,
				// which keeps a card that reveals twice from replaying stale state.
				for (let i = from; i < shown; i++) {
					const card = cards[i];
					card.style.setProperty('--reveal-index', i - from);
					card.classList.add('is-revealing');
					card.addEventListener('animationend', () => {
						card.classList.remove('is-revealing');
						card.style.removeProperty('--reveal-index');
					}, { once: true });
				}
	
				// Focus moves before the button can be hidden, so the last press
				// never drops focus to the top of the document.
				if (firstNew) {
					firstNew.setAttribute('tabindex', '-1');
					firstNew.focus();
				}
	
				footer.hidden = shown >= cards.length;
	
				if (status) {
					status.textContent = `Showing ${shown} of ${cards.length}.`;
				}
			};
	
			paint();
			footer.hidden = false;
			button.addEventListener('click', reveal);
		});
	})();
	
		(() => {
		/**
		 * Email CTA.
		 *
		 * Never submits anywhere itself: it opens the popup named in
		 * `data-email-cta` and hands the address to that popup's email field, then
		 * moves focus to the first field still empty. The browser's own email
		 * validation runs before `submit` fires, so a blank or malformed address
		 * never gets this far.
		 */
		document.querySelectorAll('form[data-email-cta]').forEach((form) => {
			form.addEventListener('submit', (event) => {
				event.preventDefault();
	
				const slug = form.dataset.emailCta;
				const dialog = document.getElementById(`popup-${slug}`);
				if (!dialog || !window.LLPopup) return;
	
				const target = dialog.querySelector('input[type="email"]');
				if (target) {
					target.value = form.elements.email.value.trim();
					// Gravity Forms' conditional logic listens for these.
					target.dispatchEvent(new Event('input', { bubbles: true }));
					target.dispatchEvent(new Event('change', { bubbles: true }));
				}
	
				window.LLPopup.open(slug);
	
				const next = Array.from(dialog.querySelectorAll('input:not([type="hidden"]), select, textarea'))
					.find((field) => !field.value && !field.disabled && field.getClientRects().length);
				if (next) next.focus();
			});
		});
	})();
	
		(() => {
		/**
		 * Icon Card — mobile accordion.
		 *
		 * Below the large breakpoint the comp shows only the icon and title, with the
		 * description behind a chevron; from there up everything is visible and the
		 * chevron is gone (CSS hides it, so it can't be reached by a click either).
		 *
		 * The collapse itself is CSS — `[aria-expanded]` on the button drives it via
		 * `data-open` on the card — so a card renders correctly with JS unavailable
		 * (open, exactly as on desktop) rather than sealed shut.
		 */
		const BREAKPOINT = 992;
	
		document.querySelectorAll('.block-icon-card').forEach((card) => {
			const toggle = card.querySelector('.block-icon-card__toggle');
			const head = card.querySelector('.block-icon-card__head');
			if (!toggle) return;
	
			// A card that's a slide stays open at every size -- the comp's mobile
			// track shows a whole card, not a collapsed row -- so it keeps neither
			// the chevron (hidden in CSS) nor the tap-to-toggle head.
			if (card.closest('.block-slider')) return;
	
			// Name the control after the card it belongs to, so a screen reader
			// running the list of buttons doesn't hear "Toggle description" nine
			// times over.
			const title = card.querySelector('.block-icon-card__title');
			if (title && title.textContent.trim()) {
				toggle.setAttribute('aria-label', `${toggle.getAttribute('aria-label')}: ${title.textContent.trim()}`);
			}
	
			const setOpen = (open) => {
				toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
				card.dataset.open = open ? 'true' : 'false';
			};
	
			setOpen(false);
	
			toggle.addEventListener('click', () => {
				setOpen(toggle.getAttribute('aria-expanded') !== 'true');
			});
	
			// The whole title row is the tap target on mobile, which is what the
			// comp's full-width card implies. Guarded on width so a desktop click on
			// the heading (e.g. to select text) doesn't fire a hidden toggle.
			if (head) {
				head.addEventListener('click', (event) => {
					if (window.innerWidth >= BREAKPOINT) return;
					if (event.target.closest('a')) return;
					setOpen(toggle.getAttribute('aria-expanded') !== 'true');
				});
			}
		});
	})();
	
		(() => {
		// The client logo row scrolls continuously. Cloning the set once means the
		// track can animate a clean -50% and land exactly where it started, so the
		// loop has no visible seam. The marquee class is only added after cloning,
		// so the row stays static if this never runs.
		document.querySelectorAll('.block-logo-grid:not(.block-logo-grid--natural):not(.block-logo-grid--wrapped)').forEach((row) => {
			const track = row.querySelector('.acf-innerblocks-container');
	
			if (!track || track.dataset.marqueeReady) {
				return;
			}
	
			const logos = Array.from(track.children);
	
			if (logos.length < 2) {
				return;
			}
	
			logos.forEach((logo) => {
				const clone = logo.cloneNode(true);
				clone.setAttribute('aria-hidden', 'true');
				clone.querySelectorAll('img').forEach((img) => {
					img.setAttribute('alt', '');
				});
				track.appendChild(clone);
			});
	
			track.dataset.marqueeReady = 'true';
			row.classList.add('is-marquee');
		});
	})();
	
		(() => {
		// Matches $bp-lg.
		const DESKTOP = 992;
		const ARROWS_HEIGHT = 40;
		const desktop = window.matchMedia(`(min-width: ${DESKTOP}px)`);
		// Also bundled into the block editor's canvas by editor-post-slider.js.
		const inEditor = Boolean(window.frameElement && window.frameElement.name === 'editor-canvas');
	
		// The comp's layout: a Columns block with a Section Header in the column
		// before this one. There the arrows go under the intro on desktop; anywhere
		// else they stay under the cards.
		const introColumnFor = (block) => {
			const column = block.closest('.wp-block-column');
			const intro = column && !column.nextElementSibling ? column.previousElementSibling : null;
			return intro && intro.classList.contains('wp-block-column') && intro.querySelector('.block-section-header') ? intro : null;
		};
	
		// Stacked, the active card is centred with its neighbours peeking either
		// side; beside the intro, the row starts on the track's left edge and runs
		// off the page. Card widths come from CSS.
		const start = (block) => {
			const viewport = block.querySelector('.block-post-slider__viewport');
			if (!viewport || viewport.swiper || typeof window.Swiper === 'undefined') {
				return;
			}
	
			new window.Swiper(viewport, {
				modules: [window.SwiperNavigation],
				slidesPerView: 'auto',
				spaceBetween: 12,
				centeredSlides: true,
				loop: false,
				// Dragging would fight the editor's own block selection.
				allowTouchMove: !inEditor,
				breakpoints: {
					[DESKTOP]: { spaceBetween: 24, centeredSlides: false },
				},
				navigation: {
					prevEl: block.querySelector('.block-post-slider__arrow--prev'),
					nextEl: block.querySelector('.block-post-slider__arrow--next'),
				},
			});
		};
	
		// In the editor the intro column belongs to React, so the arrows stay put
		// and are pinned over the room that column keeps for them.
		const pin = (block) => {
			const arrows = block.querySelector('.block-post-slider__arrows');
			const intro = introColumnFor(block);
			if (!arrows) {
				return;
			}
	
			const pinned = Boolean(intro) && desktop.matches;
			arrows.classList.toggle('is-pinned', pinned);
			if (!pinned || !arrows.offsetParent) {
				return;
			}
	
			const from = arrows.offsetParent.getBoundingClientRect();
			const to = intro.getBoundingClientRect();
			arrows.style.setProperty('--arrows-top', `${to.bottom - ARROWS_HEIGHT - from.top}px`);
			arrows.style.setProperty('--arrows-left', `${to.left - from.left}px`);
		};
	
		if (inEditor) {
			// ACF swaps the preview HTML on every change, and the canvas reflows as
			// the blocks around it are edited.
			let frame = 0;
			const refresh = () => {
				cancelAnimationFrame(frame);
				frame = requestAnimationFrame(() => {
					document.querySelectorAll('.block-post-slider').forEach((block) => {
						start(block);
						pin(block);
					});
				});
			};
	
			new MutationObserver(refresh).observe(document.documentElement, { childList: true, subtree: true });
			new ResizeObserver(refresh).observe(document.documentElement);
			return;
		}
	
		document.querySelectorAll('.block-post-slider').forEach((block) => {
			const arrows = block.querySelector('.block-post-slider__arrows');
			const intro = introColumnFor(block);
			start(block);
	
			if (arrows && intro) {
				const move = () => {
					arrows.classList.toggle('is-beside-intro', desktop.matches);
					(desktop.matches ? intro : block).append(arrows);
				};
	
				move();
				desktop.addEventListener('change', move);
			}
		});
	})();
	
		(() => {
		/**
		 * Price Toggle.
		 *
		 * Writes the chosen period onto the nearest Section (falling back to the
		 * <body>), and every Pricing Card underneath it swaps which of its two price
		 * figures is displayed -- see block-pricing-card.scss. Both figures are
		 * already in the markup, so nothing is measured, fetched, or rewritten.
		 *
		 * Scoping to the Section rather than the document means two pricing bands on
		 * one page keep their own state.
		 */
		document.querySelectorAll('[data-price-toggle]').forEach((toggle) => {
			const options = toggle.querySelectorAll('.block-price-toggle__option');
			const scope = toggle.closest('.block-section') || document.body;
	
			const apply = (period) => {
				toggle.dataset.period = period;
				scope.dataset.pricePeriod = period;
				options.forEach((option) => {
					option.setAttribute('aria-pressed', option.dataset.period === period ? 'true' : 'false');
				});
			};
	
			// The block renders its default period into the markup; mirror it onto
			// the scope up front so the two never disagree.
			apply(toggle.dataset.period || 'monthly');
	
			options.forEach((option) => {
				option.addEventListener('click', () => apply(option.dataset.period));
			});
		});
	})();
	
		(() => {
		/**
		 * Pricing Card — "See full plan details" disclosure.
		 *
		 * The collapse itself is CSS: `data-details-open` on the card hides the panel,
		 * and `aria-expanded` on the button drives the label swap and the chevron
		 * flip. This only sets the two attributes, which means a card with JS
		 * unavailable renders with its details already open rather than behind a
		 * button that does nothing.
		 */
		document.querySelectorAll('.block-pricing-card').forEach((card) => {
			const toggle = card.querySelector('.block-pricing-card__details-toggle');
			if (!toggle) return;
	
			const setOpen = (open) => {
				toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
				card.dataset.detailsOpen = open ? 'true' : 'false';
			};
	
			setOpen(false);
	
			toggle.addEventListener('click', () => {
				setOpen(toggle.getAttribute('aria-expanded') !== 'true');
			});
		});
	})();
	
		(() => {
		// Matches $bp-lg -- the testimonial track's own desktop breakpoint.
		const DESKTOP = 992;
	
		// Matches $bp-md. Where "Mobile only" hands layout back to CSS. Deliberately
		// lower than DESKTOP: by 768 there's room for two pricing cards side by side,
		// and a pair of 270px slides in a 718px track leaves obvious dead space with
		// arrows that have nothing to scroll to. Has to agree with the stylesheet.
		const STATIC_ROW = 768;
	
		// Where each "Mobile only" track hands layout back to CSS. Four step cards
		// sharing a 768px row come out around 160px wide, so that one stays a slider
		// until there's a full content column to divide.
		const staticRowFor = (type) => (type === 'step' ? DESKTOP : STATIC_ROW);
	
		const configFor = (type) => {
			// Stacked, all three are a centred track with the neighbours peeking
			// either side. Widths come from CSS, so `auto` lets the stylesheet own
			// every size.
			if (type === 'feature' || type === 'icon' || type === 'step') {
				return {
					slidesPerView: 'auto',
					spaceBetween: 12,
					centeredSlides: true,
					loop: false,
					// The run ends on a card lined up on the content column, leaving
					// the spare space past the last card, instead of Swiper's default
					// of pinning the last card to the viewport edge and cutting the
					// first visible one in half.
					snapToSlideEdge: true,
					breakpoints: {
						[DESKTOP]: { spaceBetween: 24, centeredSlides: false },
					},
				};
			}
	
			if (type === 'pricing') {
				return {
					// The comp lines the first pricing card up on the page gutter with
					// the next one peeking past the right edge, rather than centring
					// the active card the way the testimonials do. Widths come from
					// CSS, so `auto` lets the stylesheet own the mobile card size.
					slidesPerView: 'auto',
					spaceBetween: 12,
					centeredSlides: false,
					loop: false,
				};
			}
	
			return {
				slidesPerView: 1,
				// Figma sets the cards 24px apart on mobile and 88px on desktop.
				spaceBetween: 24,
				centeredSlides: true,
				loop: true,
				breakpoints: {
					[DESKTOP]: { spaceBetween: 88 },
				},
			};
		};
	
		document.querySelectorAll('.block-slider').forEach((block) => {
			const viewport = block.querySelector('.block-slider__viewport');
			const prev = block.querySelector('.block-slider__arrow--prev');
			const next = block.querySelector('.block-slider__arrow--next');
			if (!viewport || typeof window.Swiper === 'undefined') {
				return;
			}
	
			const type = block.dataset.slideType || 'testimonial';
			const options = {
				modules: [window.SwiperNavigation],
				...configFor(type),
				navigation: {
					nextEl: next,
					prevEl: prev,
				},
			};
	
			// "Always" is the original behaviour: one instance, for the life of the
			// page.
			if (block.dataset.slideOn !== 'mobile') {
				new window.Swiper(viewport, options);
				return;
			}
	
			// "Mobile only": the pricing row is a plain flex row on desktop, so Swiper
			// is torn down rather than left running-but-disabled. `enabled: false` at a
			// breakpoint would stop the interaction but keep Swiper's inline slide
			// widths and wrapper transform in place, which the static layout then has
			// to fight; destroying it removes them outright.
			const query = window.matchMedia(`(max-width: ${staticRowFor(type) - 1}px)`);
			let swiper = null;
	
			const sync = () => {
				if (query.matches && !swiper) {
					swiper = new window.Swiper(viewport, options);
				} else if (!query.matches && swiper) {
					swiper.destroy(true, true);
					swiper = null;
				}
			};
	
			sync();
			query.addEventListener('change', sync);
		});
	})();
	

	/**
	 * Visitor tracking for Gravity Forms' {user_referrer} and {user_path}
	 * merge tags (see includes/gravity-forms.php, which reads these cookies
	 * when an entry is saved).
	 *
	 * - ll_referrer: the first off-site referrer, kept for a day from the landing.
	 * - ll_path: every page viewed, newline-separated, kept for a day from the
	 *   last view. When it outgrows the cap the oldest page after the landing
	 *   page is dropped, so both ends survive.
	 */
	(() => {
		if (document.body.classList.contains('error404')) return;
	
		const read = (name) => {
			const match = document.cookie.match(`(?:^|; )${name}=([^;]*)`);
			return match ? decodeURIComponent(match[1]) : '';
		};
		const write = (name, value) => {
			const secure = location.protocol === 'https:' ? '; Secure' : '';
			document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=86400; SameSite=Lax${secure}`;
		};
	
		try {
			if (!read('ll_referrer') && document.referrer && new URL(document.referrer).host !== location.host) {
				write('ll_referrer', document.referrer);
			}
		} catch (e) {}
	
		const path = read('ll_path').split('\n').filter(Boolean);
		const here = location.pathname + location.search;
	
		if (path[path.length - 1] !== here) path.push(here);
		while (path.length > 2 && (path.length > 25 || encodeURIComponent(path.join('\n')).length > 3000)) {
			path.splice(1, 1);
		}
		write('ll_path', path.join('\n'));
	})();
	

	/**
	 * jQuery Document Ready
	 *
	 * @link https://learn.jquery.com/using-jquery-core/document-ready/
	 */
	$(() => {
		/**
		 * Main navigation.
		 *
		 * Each dropdown is a disclosure: its toggle is a button carrying
		 * `aria-expanded`. A top-level item with its own URL splits in two, a
		 * link to that page and the toggle beside it. Desktop opens on click or
		 * mouse hover; a click on a
		 * hover-opened panel pins it open. Below the breakpoint the whole card
		 * becomes a modal menu and each dropdown drills in with a Back button.
		 */
		const NAV_DESKTOP = window.matchMedia('(min-width: 1200px)');
		const siteHeader = document.querySelector('.site-header');
		const mainNav = siteHeader && siteHeader.querySelector('.main-nav');

		if (mainNav) {
			const navBar = siteHeader.querySelector('.nav-bar');
			const navToggle = siteHeader.querySelector('.nav-bar__toggle');
			const navItems = mainNav.querySelectorAll('.main-nav__item.has-panel');
			let openItem = null;
			let openedBy = null;
			let closeTimer = null;

			const triggerOf = (item) => item.querySelector('.main-nav__toggle');

			const placePanel = (panel) => {
				panel.style.setProperty('--panel-shift', '0px');
				if (!NAV_DESKTOP.matches) return;

				const bounds = navBar.getBoundingClientRect();
				const rect = panel.getBoundingClientRect();
				let shift = 0;

				if (rect.right > bounds.right) shift = bounds.right - rect.right;
				if (rect.left + shift < bounds.left) shift = bounds.left - rect.left;
				panel.style.setProperty('--panel-shift', `${shift}px`);
			};

			const closePanel = (returnFocus = false) => {
				window.clearTimeout(closeTimer);
				if (!openItem) return;

				const item = openItem;
				item.classList.remove('is-open');
				triggerOf(item).setAttribute('aria-expanded', 'false');
				mainNav.classList.remove('has-open-panel');
				openItem = null;
				openedBy = null;

				if (returnFocus) triggerOf(item).focus();
			};

			const openPanel = (item, source) => {
				window.clearTimeout(closeTimer);

				if (openItem === item) {
					if (source === 'click') openedBy = 'click';
					return;
				}

				closePanel();
				openItem = item;
				openedBy = source;
				item.classList.add('is-open');
				triggerOf(item).setAttribute('aria-expanded', 'true');
				mainNav.classList.add('has-open-panel');
				placePanel(item.querySelector('.main-nav__panel'));

				if (!NAV_DESKTOP.matches) {
					mainNav.scrollTop = 0;
					mainNav.scrollLeft = 0;
					item.querySelector('.main-nav__back').focus({ preventScroll: true });
				}
			};

			navItems.forEach((item) => {
				triggerOf(item).addEventListener('click', () => {
					if (openItem === item && openedBy !== 'hover') {
						closePanel();
					} else {
						openPanel(item, 'click');
					}
				});

				item.addEventListener('pointerenter', (event) => {
					if (event.pointerType === 'mouse' && NAV_DESKTOP.matches) openPanel(item, 'hover');
				});

				item.addEventListener('pointerleave', (event) => {
					if (event.pointerType !== 'mouse' || openItem !== item || openedBy !== 'hover') return;
					closeTimer = window.setTimeout(closePanel, 200);
				});

				item.querySelector('.main-nav__back').addEventListener('click', () => closePanel(true));
			});

			mainNav.addEventListener('focusout', (event) => {
				if (NAV_DESKTOP.matches && openItem && event.relatedTarget && !openItem.contains(event.relatedTarget)) {
					closePanel();
				}
			});

			document.addEventListener('click', (event) => {
				if (openItem && NAV_DESKTOP.matches && !openItem.contains(event.target)) closePanel();
			});

			const isMenuOpen = () => siteHeader.classList.contains('is-open');

			// Everything outside the header goes inert, so focus and the
			// screen-reader cursor stay inside the open menu.
			const setPageInert = (inert) => {
				Array.from(document.body.children).forEach((el) => {
					if (el !== siteHeader && el.tagName !== 'SCRIPT') el.inert = inert;
				});
			};

			const openMenu = () => {
				siteHeader.style.minHeight = `${siteHeader.offsetHeight}px`;
				siteHeader.classList.add('is-open');
				navToggle.setAttribute('aria-expanded', 'true');
				navToggle.setAttribute('aria-label', 'Close menu');
				document.documentElement.classList.add('has-nav-open');
				setPageInert(true);
			};

			const closeMenu = () => {
				closePanel();
				siteHeader.classList.remove('is-open');
				siteHeader.style.minHeight = '';
				navToggle.setAttribute('aria-expanded', 'false');
				navToggle.setAttribute('aria-label', 'Open menu');
				document.documentElement.classList.remove('has-nav-open');
				setPageInert(false);
			};

			navToggle.addEventListener('click', () => (isMenuOpen() ? closeMenu() : openMenu()));

			// A link may open a popup, which can't take focus while the menu
			// holds the rest of the page inert.
			navBar.addEventListener('click', (event) => {
				if (!event.target.closest('a')) return;
				if (isMenuOpen()) closeMenu();
				else closePanel();
			});

			document.addEventListener('keydown', (event) => {
				if (event.key !== 'Escape' || document.querySelector('dialog[open]')) return;

				if (openItem) {
					closePanel(true);
				} else if (isMenuOpen()) {
					closeMenu();
					navToggle.focus();
				}
			});

			NAV_DESKTOP.addEventListener('change', () => {
				if (isMenuOpen()) closeMenu();
				else closePanel();
			});
		}

		/**
		 * Footer link groups: an accordion below 768px, where each group
		 * title is a disclosure button. The markup ships them expanded, so
		 * the links stay reachable if this never runs.
		 */
		document.querySelectorAll('.footer-nav-group__toggle').forEach((toggle) => {
			toggle.setAttribute('aria-expanded', 'false');

			toggle.addEventListener('click', () => {
				toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
			});
		});

		/**
		 * Modal dialogs: the demo popup and the video lightbox.
		 *
		 * Both are native <dialog>s carrying `ll-dialog`, which is what the
		 * shared plumbing below binds to -- so they share one scroll lock,
		 * one backdrop-click rule and one focus-restore path rather than
		 * keeping two that can drift apart. <dialog> itself supplies the
		 * accessibility contract: aria-modal semantics, a focus trap,
		 * Esc-to-close, and the rest of the page going inert.
		 *
		 * A popup opens from any link -- added through the block editor's
		 * normal URL field, a nav menu item, or written by hand -- pointing
		 * to `#popup-{slug}`, the same convention Popup Maker uses
		 * (`#popmake-{id}`), so editors never need custom block-editor UI
		 * to trigger one. It's a real anchor fragment matching the
		 * dialog's own `id` -- not a colon-delimited pseudo-scheme, since
		 * `esc_url()` silently strips anything shaped like `word:` as an
		 * unrecognized protocol (confirmed: `#popup:demo-request` came back
		 * empty through the ACF link field's `esc_url()` call in
		 * navigation.php). `window.LLPopup.open()`/`.close()` cover
		 * triggers that aren't an <a> (e.g. a button's onclick).
		 */
		let dialogLastFocused = null;

		const showDialog = (dialog) => {
			if (!dialog || typeof dialog.showModal !== 'function') return;

			dialogLastFocused = document.activeElement;
			document.documentElement.classList.add('has-popup-open');
			dialog.showModal();
		};

		const openPopup = (slug) => {
			showDialog(document.getElementById(`popup-${slug}`));
		};

		const closePopup = (slug) => {
			const dialog = document.getElementById(`popup-${slug}`);
			if (dialog && dialog.open) dialog.close();
		};

		/**
		 * The video lightbox.
		 *
		 * One dialog serves every Video Card on the page (see
		 * template-parts/parts/video-lightbox.php). The embed URL rides on
		 * the card's play button and is written into the iframe only here,
		 * on open -- so the page asks nothing of the video host until a
		 * visitor asks for the video -- then blanked again on close, which
		 * is also what stops playback.
		 */
		const videoLightbox = document.querySelector('.ll-video-lightbox');

		const openVideoLightbox = (button) => {
			if (!videoLightbox) return;

			const iframe = videoLightbox.querySelector('.ll-video-lightbox__iframe');
			const src = button.dataset.videoSrc;
			if (!iframe || !src) return;

			// Naming the dialog and its frame after the video means a screen
			// reader announces which one opened, not just "video player".
			const title = button.dataset.videoTitle;
			if (title) {
				iframe.setAttribute('title', title);
				videoLightbox.setAttribute('aria-label', title);
			}

			iframe.setAttribute('src', src);
			showDialog(videoLightbox);
		};

		document.addEventListener('click', (event) => {
			const popupTrigger = event.target.closest('a[href^="#popup-"]');
			if (popupTrigger) {
				event.preventDefault();
				openPopup(popupTrigger.getAttribute('href').slice('#popup-'.length));
				return;
			}

			const playButton = event.target.closest('.block-video-card__play');
			if (playButton) {
				openVideoLightbox(playButton);
				return;
			}

			const closeButton = event.target.closest('[data-popup-close]');
			if (closeButton) {
				const dialog = closeButton.closest('dialog.ll-dialog');
				if (dialog) dialog.close();
				return;
			}

			// Click on the dialog's own backdrop area (outside its content).
			const openDialog = event.target.closest('dialog.ll-dialog[open]');
			if (openDialog && event.target === openDialog) {
				openDialog.close();
			}
		});

		document.querySelectorAll('dialog.ll-dialog').forEach((dialog) => {
			dialog.addEventListener('close', () => {
				document.documentElement.classList.remove('has-popup-open');

				// Unloading the player is what stops the sound. Left in
				// place, a paused video would be one reopen away from
				// picking up mid-sentence.
				const iframe = dialog.querySelector('.ll-video-lightbox__iframe');
				if (iframe) iframe.removeAttribute('src');

				if (dialogLastFocused && typeof dialogLastFocused.focus === 'function') {
					dialogLastFocused.focus();
				}
				dialogLastFocused = null;
			});
		});

		window.LLPopup = {
			open: openPopup,
			close: closePopup,
		};

		/**
		 * Hero glow parallax.
		 *
		 * The blurred chevron behind the hero
		 * (`.block-hero.is-style-homepage::before`, see block-hero.scss) drifts
		 * down at a fraction of the scroll distance, so it
		 * appears to sit behind the page rather than travel with it. The
		 * script only writes a length into `--hero-glow-shift`; the transform
		 * that consumes it lives in the stylesheet, which keeps the mobile
		 * rotation and the desktop placement each in one place.
		 *
		 * The offset tracks absolute scroll position rather than the hero's
		 * own rect: the glow starts 916px above the hero and runs 2573px tall,
		 * so it is still on screen well after the hero itself has left, and a
		 * rect-based offset would jump when the hero unpins.
		 *
		 * CURRENTLY OFF -- flip HERO_PARALLAX_ENABLED to `true` and rebuild
		 * (`npx gulp scripts`) to switch it back on; nothing else needs to
		 * change. With it off the script never runs, so `--hero-glow-shift`
		 * keeps its stylesheet default of 0px and the glow sits exactly where
		 * the Figma comp puts it. The `.has-hero-parallax` class the script
		 * adds is what carries the `will-change: transform` layer promotion in
		 * block-hero.scss, so that cost is not paid while the effect is disabled.
		 */
		const HERO_PARALLAX_ENABLED = false;

		const heroSection = document.querySelector('.block-hero.is-style-homepage');
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

		if (HERO_PARALLAX_ENABLED && heroSection && !prefersReducedMotion.matches) {
			heroSection.classList.add('has-hero-parallax');

			// Low enough that the movement reads as depth rather than as an
			// element sliding out of position.
			const PARALLAX_FACTOR = 0.1;
			let pending = false;

			const applyShift = () => {
				pending = false;
				const scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;
				heroSection.style.setProperty('--hero-glow-shift', `${(scrolled * PARALLAX_FACTOR).toFixed(2)}px`);
			};

			const requestShift = () => {
				if (pending) return;
				pending = true;
				window.requestAnimationFrame(applyShift);
			};

			window.addEventListener('scroll', requestShift, { passive: true });
			// Covers a reload part-way down the page and Safari's restored
			// scroll position, both of which land without firing `scroll`.
			applyShift();

			// If the visitor turns reduced motion on mid-session, drop the
			// offset and stop listening.
			const stopParallax = (event) => {
				if (!event.matches) return;
				window.removeEventListener('scroll', requestShift);
				heroSection.classList.remove('has-hero-parallax');
				heroSection.style.setProperty('--hero-glow-shift', '0px');
			};

			if (typeof prefersReducedMotion.addEventListener === 'function') {
				prefersReducedMotion.addEventListener('change', stopParallax);
			}
		}
	});

})(jQuery);
